// The forced-state mechanism, shared by every kit component.
//
// A component reaches most of its own states: :hover, :focus-visible, :active
// and :disabled are the browser's job and the CSS matches them. Three things
// are not:
//
//   1. A screenshot cannot hold :hover. Playwright can hover one element per
//      page; the Phase 1 gate needs every state of every component in one
//      frame, in both themes.
//   2. Some states are not pseudo-classes at all — `locked`, `dragover`,
//      `uploading`, `truncated`, `indeterminate`.
//   3. A reviewer opening the gallery wants to SEE the six button states side
//      by side, which is exactly how the specimen presents them.
//
// So every component takes an optional `state` and renders it as `data-state`,
// and kit.css matches the pseudo-class and the attribute in the same rule.
// The forced state is the same paint as the real one, never a copy of it,
// because there is one declaration serving both selectors.

/** Every state name that appears in a data-states attribute in Kits 01 and 02. */
export type KitState =
  | "rest"
  | "hover"
  | "focus"
  | "active"
  | "disabled"
  | "loading"
  | "selected"
  | "filled"
  | "empty"
  | "error"
  | "readonly"
  | "open"
  | "checked"
  | "indeterminate"
  | "on"
  | "off"
  | "dragover"
  | "uploading"
  | "locked"
  | "expanded"
  | "collapsed"
  | "complete"
  | "current"
  | "upcoming"
  | "truncated";

export interface Stateful {
  /**
   * Force a visual state. For the gallery and for screenshot tests; production
   * code should leave this undefined and let the browser drive.
   */
  state?: KitState;
}

/**
 * `rest` is the absence of a state, not a state. Emitting data-state="rest"
 * would make the attribute selector list in kit.css need a rest branch that
 * says nothing, so it is dropped here instead.
 */
export function stateAttr(state?: KitState): { "data-state"?: KitState } {
  return state && state !== "rest" ? { "data-state": state } : {};
}

/** True when a forced state should also disable the real control. */
export function isInert(state?: KitState): boolean {
  return state === "disabled" || state === "loading";
}

/** Join class names. Falsy entries drop out. */
export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}
